import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import { type Column, type RowData } from "@tanstack/react-table";
import { useState } from "react";
import { Tooltip } from "../../Tooltip";
import { cn } from "../../../utils";

type SortableHeaderCellProps<T extends RowData> = {
  column: Column<T, unknown>;
  children: React.ReactNode;
  tooltipContent?: string;
};

export const SortableHeaderCell = <T extends RowData>({
  column,
  children,
  tooltipContent,
}: SortableHeaderCellProps<T>) => {
  const [isHovered, setIsHovered] = useState(false);
  const canSort = column.getCanSort();
  const sortDirection = column.getIsSorted();
  let content = children;
  const iconClassName =
    "size-4 text-icon-panel-neutral-mild hover:text-icon-action-neutral-hover";

  if (tooltipContent) {
    content = (
      <div className="flex items-center gap-1.5">
        <span>{children}</span>
        <Tooltip
          contentProps={{ className: "max-w-50" }}
          content={tooltipContent}
        >
          <Info className={cn(iconClassName, "ml-0.5")} />
        </Tooltip>
      </div>
    );
  }

  if (!canSort) {
    return <span>{content}</span>;
  }

  const renderSortIcon = () => {
    if (sortDirection === "asc") {
      return <ArrowUp className={iconClassName} />;
    }

    if (sortDirection === "desc") {
      return <ArrowDown className={iconClassName} />;
    }

    return (
      <ChevronsUpDown
        className={cn(iconClassName, { invisible: !isHovered })}
      />
    );
  };

  return (
    <span
      className="flex gap-1.5 items-center cursor-pointer"
      onClick={() => column.toggleSorting(sortDirection === "asc")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
    >
      {content}
      {renderSortIcon()}
    </span>
  );
};
